import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { apiFetch } from '@/api/client'
import type { LoginHistoryRow } from '@/api/map/memberDetailLab'

/** Agent-API `POST /v1/userLog/getuserLog` — Lab UserLog/getuserLog. */
export type GetUserLogRequest = {
  memberId: string
  fromDate?: string
  toDate?: string
  page?: number
  pageSize?: number
}

export type UserLogDeviceApi = {
  device?: { type?: string; brand?: string; model?: string }
  client?: { name?: string; version?: string }
  os?: { name?: string; version?: string; platform?: string }
}

export type UserLogQualityApi = {
  ISP?: string
  city?: string
  [k: string]: unknown
}

/** One Lab/Agent-API UserLog row. */
export type UserLogApiRow = {
  id?: string
  _id?: string
  user?: { username?: string; _id?: string; id?: string }
  createdAt?: string
  ipAddress?: string
  device?: UserLogDeviceApi | null
  qualityScore?: UserLogQualityApi | null
}

/** Lab sails envelope from hierarchy-service. */
export type GetUserLogResponse = {
  value: boolean
  data: {
    max?: number
    data?: UserLogApiRow[]
    count?: number
  }
  error?: string
}

export type LoginHistoryResult = {
  rows: LoginHistoryRow[]
  total: number
  page: number
  pageSize: number
}

export type MemberBlockedDeviceIdResponse = {
  value: boolean
  data?: { msg?: string; blocked?: number; deviceIds?: string[] }
  error?: string
}

/**
 * Lab `UserLog/getuserLog` → Agent-API hierarchy.
 */
export async function getUserLog(body: GetUserLogRequest): Promise<GetUserLogResponse> {
  const out: Record<string, unknown> = {
    memberId: body.memberId,
    page: body.page ?? 1,
    pageSize: body.pageSize ?? DEFAULT_PAGE_SIZE,
  }
  if (body.fromDate) out.fromDate = body.fromDate
  if (body.toDate) out.toDate = body.toDate
  return apiFetch<GetUserLogResponse>('/v1/userLog/getuserLog', {
    method: 'POST',
    body: JSON.stringify(out),
  })
}

/** Winner `blockedDeviceId/saveblockedDeviceId` — member-scoped. */
export async function saveBlockedDeviceId(body: {
  memberId: string
  comment?: string
}): Promise<MemberBlockedDeviceIdResponse> {
  return apiFetch<MemberBlockedDeviceIdResponse>('/v1/blockedDeviceId/saveblockedDeviceId', {
    method: 'POST',
    body: JSON.stringify({
      memberId: body.memberId,
      comment: body.comment?.trim() ?? '',
    }),
  })
}

/** Winner `blockedDeviceId/deleteblockedDeviceId` — member-scoped. */
export async function deleteBlockedDeviceId(body: {
  memberId: string
}): Promise<MemberBlockedDeviceIdResponse> {
  return apiFetch<MemberBlockedDeviceIdResponse>('/v1/blockedDeviceId/deleteblockedDeviceId', {
    method: 'POST',
    body: JSON.stringify({ memberId: body.memberId }),
  })
}

export function toLoginHistoryResult(
  live: GetUserLogResponse,
  query: { page?: number; pageSize?: number } = {},
): LoginHistoryResult {
  const pageSize = live.data?.max ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  const page = query.page ?? 1
  const total = live.data?.count ?? 0
  const rows = (live.data?.data ?? []).map(mapRow)
  return { rows, total, page, pageSize }
}

export function mapRow(row: UserLogApiRow): LoginHistoryRow {
  const device = row.device ?? null
  const quality = row.qualityScore ?? {}
  return {
    id: String(row.id ?? row._id ?? ''),
    name: String(row.user?.username ?? ''),
    at: String(row.createdAt ?? ''),
    ip: String(row.ipAddress ?? ''),
    device: formatDevice(device),
    browser: formatClient(device),
    os: formatOs(device),
    isp: String(quality.ISP ?? ''),
    city: String(quality.city ?? ''),
    qualityScore: quality as Record<string, unknown>,
  }
}

function formatDevice(device: UserLogDeviceApi | null): string {
  if (!device?.device) return ''
  const { type, brand, model } = device.device
  return [type, brand, model].filter(Boolean).join(' ').trim()
}

function formatClient(device: UserLogDeviceApi | null): string {
  if (!device?.client) return ''
  const { name, version } = device.client
  return [name, version].filter(Boolean).join(' ').trim()
}

function formatOs(device: UserLogDeviceApi | null): string {
  if (!device?.os) return ''
  const { name, version, platform } = device.os
  return [name, version, platform].filter(Boolean).join(' ').trim()
}
